import { useTranslation } from 'react-i18next';

import { SelectFilter } from '@/components/common/SelectFilter';
import { MODE_ORDER, modeLabelKey, type ModeBucket } from '@/lib/battleMode';

export type ResultFilter = 'all' | 'win' | 'loss' | 'draw';
export type ModeFilter = 'all' | ModeBucket;

interface Props {
  result: ResultFilter;
  modeF: ModeFilter;
  onResult: (r: ResultFilter) => void;
  onMode: (m: ModeFilter) => void;
  /** Only render mode options for buckets actually present in the window. */
  availableModes: ModeBucket[];
}

/**
 * I filtri delle battaglie: due menu etichettati su una riga sola.
 *
 * La grafica e il comportamento stanno in `SelectFilter`, condiviso con gli
 * altri filtri dell'app (Top Deck): una seconda copia sarebbe divergita alla
 * prima modifica.
 */
export function BattlesFilters({ result, modeF, onResult, onMode, availableModes }: Props) {
  const { t } = useTranslation('cr');
  const results: ResultFilter[] = ['all', 'win', 'loss', 'draw'];
  const modes = MODE_ORDER.filter((m) => availableModes.includes(m));

  return (
    <div className="flex items-center gap-2">
      <SelectFilter
        label={t('battles.filter.resultLabel', { defaultValue: 'Risultato' })}
        value={result}
        onChange={onResult}
        options={results.map((r) => ({
          value: r,
          label: t(`battles.filter.${r}`, { defaultValue: r }),
        }))}
      />

      {/* Il menu delle modalità compare solo se ce n'è più di una da scegliere:
          un filtro con una sola opzione non filtra, occupa e basta. */}
      {modes.length > 1 && (
        <SelectFilter
          label={t('battles.filter.modeLabel', { defaultValue: 'Modalità' })}
          value={modeF}
          onChange={onMode}
          options={[
            { value: 'all' as ModeFilter, label: t('battles.filter.all', { defaultValue: 'Tutte' }) },
            ...modes.map((m) => ({
              value: m as ModeFilter,
              label: t(modeLabelKey(m), { defaultValue: m }),
            })),
          ]}
        />
      )}
    </div>
  );
}
